import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../Components/Header/Header";
import Footer from "../Components/Footer/Footer";
import { getBrand } from "../actions/ApiCalls";
import ProductFilter from "../Components/ProductFilter";
import { IMAGE_URL} from "../path";

function Brand() {
    const { brand } = useParams<{ brand?: string }>();
    const [brandProducts, setBrandProducts] = useState<any>({});
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [filterMode, setFilterMode] = useState<'hidden' | 'scroll' | 'fixed'>('hidden');
    const [selectedRangeId, setSelectedRangeId] = useState<number | null>(null);
    const [imageRatios, setImageRatios] = useState<{ [key: string]: number }>({});

    const filterRef = useRef<HTMLDivElement>(null);
    const productsRef = useRef<HTMLDivElement>(null);

    const wineURL = 'https://vinimark.s3.amazonaws.com/web/';
    const internalPublic = IMAGE_URL ;

    const navigate = useNavigate();
    const customer_code = localStorage.getItem('customer_code');

    useEffect(() => {
        window.scrollTo(0, 0);
        if (brand) {
            getBrand(brand)
                .then(response => {
                    setBrandProducts(response.data);
                })
                .catch(error => {
                    console.error("Failed to fetch brand products:", error);
                });
        }
    }, [brand]);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Combined observer logic
    useEffect(() => {
        if (!filterRef.current || !productsRef.current) return;

        const observer = new IntersectionObserver((entries) => {
            let filterVisible = false;
            let productsVisible = false;

            entries.forEach(entry => {
                if (entry.target === filterRef.current) {
                    filterVisible = entry.isIntersecting;
                }
                if (entry.target === productsRef.current) {
                    productsVisible = entry.isIntersecting;
                }
            });

            if (filterVisible) {
                setFilterMode("scroll");
            } else if (productsVisible) {
                setFilterMode("fixed");
            } else {
                setFilterMode("hidden");
            }
        }, { threshold: 0.1 });

        observer.observe(filterRef.current);
        observer.observe(productsRef.current);

        return () => {
            observer.disconnect();
        };
    }, []);

    const calculateImageDimensions = (imageRatio: number, containerHeight: number) => {
        const maxHeight = containerHeight - 100;
        const maxWidth = '90%';

        if (imageRatio > 1.5) {
            return {
                height: `${maxHeight}px`,
                width: 'auto',
                maxWidth
            };
        } else {
            return {
                height: 'auto',
                width: maxWidth,
                maxHeight: `${containerHeight < 500 ? containerHeight * 0.6 : containerHeight * 0.7}px`
            };
        }
    };

    const handleImageClick = (productSearchName: string) => {
        if (brand) {
            navigate(`/our-wines/${brand}/${productSearchName}`);
        }
    };

    const handleViewPortfolioClick = (e: React.MouseEvent) => {
        e.preventDefault();
        if (productsRef.current) {
            const offsetTop = productsRef.current.offsetTop - 100;
            window.scrollTo({ top: offsetTop, behavior: 'smooth' });
        }
    };

    const formatPrice = (price: number | string): string => {
        const numPrice = typeof price === 'string' ? parseFloat(price) : price;
        return `R${numPrice.toFixed(2)}`;
    };

    const isDesktop = windowWidth > 835;

    const filteredProducts = selectedRangeId
        ? brandProducts?.grouped_products?.find((group: any) => group.range?.id === selectedRangeId)?.products || []
        : brandProducts?.grouped_products?.flatMap((group: any) => group.products) || [];

    function getDesktopLayout() {
        const boxWidth = window.innerWidth * 0.25 - 25; // width if 4 per row

        return filteredProducts.map((product: any) => (
            <div
                key={product.id}
                style={{
                    flex: `0 0 ${boxWidth}px`, // fixed width so it doesn't grow
                    textAlign: 'center',
                    paddingBottom: '10px',
                    maxHeight: '750px',
                    width: `${boxWidth}px`, // explicit width
                    cursor: 'pointer',
                }}
                onClick={() => handleImageClick(product.search_name)}
            >
                <div
                    style={{
                        width: '100%',
                        backgroundColor: '#f5f5f5',
                        height: '600px',
                        position: 'relative',
                        padding: '0 30px',
                        boxSizing: 'border-box',
                    }}
                >
                    <img
                        src={wineURL + product.image_path}
                        alt={product.name}
                        onLoad={(e) => {
                            const img = e.target as HTMLImageElement;
                            const ratio = img.naturalHeight / img.naturalWidth;
                            setImageRatios(prev => ({ ...prev, [product.id]: ratio }));
                        }}
                        style={{
                            position: 'absolute',
                            bottom: '50px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            ...calculateImageDimensions(imageRatios[product.id] || 1.5, 600),
                            objectFit: 'contain'
                        }}
                    />
                </div>
                <p style={textLight}>{brandProducts?.name}</p>
                <p style={productName}>{product.name}</p>
                <p style={productYear}>{product.year}</p>
                {customer_code && product.price_bottle && (
                    <p style={priceText}>{formatPrice(product.price_bottle)}</p>
                )}
            </div>
        ));
    }

    function getMobileLayout() {
        return filteredProducts.map((product: any) => (
            <div
                key={product.id}
                style={{
                    width: '100%',
                    textAlign: 'center',
                    paddingBottom: '30px',
                    maxHeight: '750px',
                }}
            >
                <div
                    style={{
                        width: '100%',
                        backgroundColor: '#f5f5f5',
                        height: '400px',
                        position: 'relative',
                        padding: '0 30px',
                        boxSizing: 'border-box',
                    }}
                    onClick={() => handleImageClick(product.search_name)}
                >
                    <img
                        src={wineURL + product.image_path}
                        alt={product.name}
                        onLoad={(e) => {
                            const img = e.target as HTMLImageElement;
                            const ratio = img.naturalHeight / img.naturalWidth;
                            setImageRatios(prev => ({ ...prev, [product.id]: ratio }));
                        }}
                        style={{
                            position: 'absolute',
                            bottom: '50px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            ...calculateImageDimensions(imageRatios[product.id] || 1.5, 400),
                            objectFit: 'contain'
                        }}
                    />
                </div>
                <p style={textLight}>{brandProducts?.name}</p>
                <p style={productName}>{product.name}</p>
                <p style={productYear}>{product.year}</p>
                {customer_code && product.price_bottle && (
                    <p style={priceText}>{formatPrice(product.price_bottle)}</p>
                )}
            </div>
        ));
    }

    return (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            // overflow: 'hidden',
            position: 'relative',
            width: '100%'
        }}>
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 1000,
                background: '#fff'
            }}>
                <Header />
            </div>

            <main style={{
                flex: '1',
                paddingTop: windowWidth <= 575 ? '70px' : '90px',
                paddingBottom: '80px',
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                position: 'relative'
            }}>
                <div style={{
                    maxWidth: '100%',
                    margin: '0 auto',
                    width: '100%',
                    position: 'relative'
                }}>
                    {/* Brand Info Section */}
                    <div style={{
                        display: 'flex',
                        flexDirection: windowWidth > 575 ? 'row' : 'column',
                        margin: '0 auto',
                        width: '100%',
                        paddingBottom: '72px'
                    }}>
                        <div style={{ flex: 1 }}></div>
                        <div style={{ flex: 1 }}>
                            <div style={{
                                paddingLeft: isDesktop ? '0' : '24px',
                                paddingRight: '24px',
                                width: windowWidth > 575 ? '60%' : '100%',
                                textAlign: 'left',
                                marginLeft: isDesktop ? '26px' : '0'
                            }}>
                                <img
                                    src={wineURL + brandProducts?.brand?.image_path}
                                    alt={brandProducts?.brand?.name}
                                    style={{ width: '150px', marginBottom: '24px' }}
                                />
                                <p
                                    style={{ ...textLight, width: '90%' }}
                                    dangerouslySetInnerHTML={{
                                        __html: brandProducts?.brand?.content || ''
                                    }}
                                />
                                <div style={{ display: 'flex', gap: '10px', marginTop: '20px', flexWrap: 'wrap' }}>
                                    {brandProducts?.brand?.instagram_url && (
                                        <a style={linkStyle} href={brandProducts.brand.instagram_url} target="_blank" rel="noreferrer">INSTAGRAM</a>
                                    )}
                                    {brandProducts?.brand?.facebook_url && (
                                        <a style={linkStyle} href={brandProducts.brand.facebook_url} target="_blank" rel="noreferrer">FACEBOOK</a>
                                    )}
                                    {brandProducts?.brand?.twitter_url && (
                                        <a style={linkStyle} href={brandProducts.brand.twitter_url} target="_blank" rel="noreferrer">X.COM</a>
                                    )}
                                    {brandProducts?.brand?.website_url && (
                                        <a style={linkStyle} href={brandProducts.brand.website_url} target="_blank" rel="noreferrer">WEBSITE</a>
                                    )}
                                    <a
                                        style={{ ...linkStyle, cursor: 'pointer', textDecoration: 'underline' }}
                                        onClick={handleViewPortfolioClick}
                                    >
                                        VIEW PORTFOLIO
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Hero Image */}
                    {brandProducts?.brand?.hero_images?.[0]?.hero_image && (
                        <div style={{ width: '100%', paddingBottom: '40px' }}>
                            <img
                                src={internalPublic + brandProducts?.brand?.hero_images?.[0]?.hero_image}
                                alt={brandProducts?.name}
                                style={{
                                    width: '100%',
                                    display: 'block',
                                    verticalAlign: 'middle'
                                }}
                            />
                        </div>
                    )}

                    {/* Range Filter Buttons */}
                    {brandProducts?.grouped_products?.length > 1 && (
                        <div style={{
                            padding: '20px',
                            width: '100%',
                            boxSizing: 'border-box'
                        }}>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                flexWrap: 'wrap',
                                gap: '8px',
                                maxWidth: `${window.innerWidth - 20}px`,
                                margin: '0 auto'
                            }}>
                                {brandProducts.grouped_products.map((group: any) => {
                                    let buttonWidth = '95%';
                                    if (windowWidth > 1300) {
                                        buttonWidth = '13.8%';
                                    } else if (windowWidth >= 800) {
                                        buttonWidth = '30%';
                                    }

                                    return (
                                        <button
                                            key={group.range?.id}
                                            onClick={() =>
                                                setSelectedRangeId(prev =>
                                                    prev === group.range?.id ? null : group.range?.id
                                                )
                                            }
                                            style={{
                                                width: buttonWidth,
                                                minWidth: '100px',
                                                height: '40px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                textAlign: 'center',
                                                whiteSpace: 'normal',
                                                padding: '8px 12px',
                                                border: '1px solid #000',
                                                backgroundColor:
                                                    selectedRangeId === group.range?.id ? '#000' : '#fff',
                                                color:
                                                    selectedRangeId === group.range?.id ? '#fff' : '#000',
                                                cursor: 'pointer',
                                                boxSizing: 'border-box',
                                                fontSize: '13px',
                                            }}
                                        >
                                            {group.range?.name}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Products Grid */}
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center', // centers blue div horizontally

                        }}
                    >
                        <div
                            ref={productsRef}
                            style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                justifyContent: 'flex-start', // keeps content inside left-aligned
                                gap: '40px 20px',
                                padding: '30px 20px',
                                maxWidth: '5000px', // set your desired max width for centering
                                width: '100%',
                                boxSizing: 'border-box',

                            }}
                            id="products"
                        >
                            {isDesktop ? getDesktopLayout() : getMobileLayout()}
                        </div>
                    </div>


                    {/* Product Filter */}
                    <div ref={filterRef} style={{ width: '100%' }}>
                        <ProductFilter
                            mode={filterMode}
                            additionalFields={true}
                            brand={brandProducts?.brand?.name}
                        />
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
}

// Style Constants
const textLight: React.CSSProperties = {
    fontWeight: 400,
    marginBottom: 0,
    fontSize: '13px',
    lineHeight: '16.9px',
    width: '90%'
};

const productName: React.CSSProperties = {
    fontWeight: 600,
    textTransform: 'uppercase',
    marginTop: 0,
    marginBottom: 0,
    fontSize: '13px',
    lineHeight: '16.9px',
};

const productYear: React.CSSProperties = {
    fontWeight: 600,
    textTransform: 'uppercase',
    marginTop: 0,
    marginBottom: 0,
    fontSize: '13px',
    lineHeight: '16.9px',
};

const priceText: React.CSSProperties = {
    fontSize: '13px',
    lineHeight: '16.9px',
    marginTop: 0,
    fontWeight: 400,
};

const linkStyle: React.CSSProperties = {
    color: 'black',
    fontSize: '10px',
    fontFamily: "'Vinimark-Diatype', sans-serif",
    fontWeight: 600,
};

export default Brand;
